import { computed, inject } from "@angular/core"
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals"
import { addEntities, setAllEntities, setEntities, updateEntity, withEntities } from "@ngrx/signals/entities"
import { ClinicService } from "../../services/clinic/clinic.service"
import { UserFacade } from "../user/user.facade"
import { rxMethod } from "@ngrx/signals/rxjs-interop"
import { exhaustMap, filter, pipe, tap } from "rxjs"
import { tapResponse } from "@ngrx/operators"
import { extractErrorMessage } from "../../interceptors/helpers/extract-error-message.helper"
import { ClinicStore } from "./clinic.store"

interface ClinicLocation {
    id: number;
    clinic_id: number;
    address: string;
    address2: string;
    city: string;
    state: string;
    zip_code: string;
    phone: string;
    active: number
    primary_location?: boolean | number | string
    is_primary?: boolean | number | string
    primary?: boolean | number | string
    created_at: string
    updated_at: string
    end_date: string
}

interface ClinicAdminState {
    loading: boolean
    locationListStatus: string,
    pendingLocationAdd: null | any,
    activeClinicId: number,
    error: {
        error: boolean, message: string
    }
}

export const ClinicLocationStore = signalStore(
    {providedIn: 'root'},
    withEntities<ClinicLocation>(),
    withState<ClinicAdminState>({
        loading: false,
        locationListStatus: 'idle',
        pendingLocationAdd: null,
        activeClinicId: 0,
        error: {
            error: false, message: ''
        }
    }),
    withProps((store) => ({
        clinicStore: inject(ClinicStore),
        clinicService: inject(ClinicService),
        userFacade: inject(UserFacade)
    })),
    withComputed((store) => ({
        locations: computed(() =>
  Object.values(store.entities()).filter((location: any) => {
    const clinic_id = store.activeClinicId() > 0
      ? store.activeClinicId()
      : store.userFacade.clinic_id();

    const sameClinic = Number(location.clinic_id) === Number(clinic_id);

    const isActive =
      location.active === true ||
      location.active === 1 ||
      location.active === '1';

    const hasNoEndDate =
      !location.end_date &&
      !location.endDate;

    return sameClinic && isActive && hasNoEndDate;
  })
)
    })),
    withMethods((store) => ({
        clear: function() {
            patchState(store, {
                entityMap: {},
                ids: [],
            });
        },
        clearError: function() {

            patchState(store, {
                error: {
                    error: false, message: ''
                }
            })
        },
        setError(error: boolean, message: string) {
            patchState(store, {
                error: {
                    error, message
                }
            })
        },
        setPendingAddlocation(pendingLocationAdd: any) {
            patchState(store, {
                pendingLocationAdd
            })
        },
        setClinicLocations(locations: any) {
            patchState(store, setEntities(locations))
        },
        setLocationListStatus(locationListStatus: string) {
            patchState(store, { locationListStatus})
        },
        setActiveClinicId(activeClinicId: number) {
            patchState(store, { activeClinicId })
        }
    })),
    withMethods((store) => ({
        addClinicLocation: rxMethod<ClinicLocation>(
            pipe(
                tap((dto) => {
                    store.clearError()
                    store.setLocationListStatus('loading')
                }),

                exhaustMap((dto) => 
                    store.clinicService.saveLocation(dto).pipe(
                        tapResponse({
                            next: (location: any) => {
                                patchState(store, addEntities([location]))
                                store.setPendingAddlocation(location)
                                store.clinicStore.setLocation(location)
                                store.setLocationListStatus('success')
                            },
                            error: (err: unknown) => {
                                store.setError(true, extractErrorMessage(err))
                                store.setLocationListStatus('error')
                            }
                        })
                    )
                )
            )
        ),
        editClinicLocation: rxMethod<ClinicLocation>(
            pipe(
                tap((dto) => {
                    store.clearError()
                    store.setLocationListStatus('loading')
                }),
                exhaustMap((dto) => 
                    store.clinicService.saveLocation(dto).pipe(
                        tapResponse({
                            next: (location: any) => {
                                patchState(store, updateEntity({
                                    id: location.id,
                                    changes: location,
                                }));
                                store.clinicStore.updateExistingLocation(location)
                                store.setLocationListStatus('success')
                            },
                            error: (err: unknown) => {
                                store.setError(true, extractErrorMessage(err))
                                store.setLocationListStatus('error')
                            }
                        })
                    )
                )
            )
        ),

        setPrimaryClinicLocation: rxMethod<{location: ClinicLocation, onSuccess?: () => void}>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setLocationListStatus('loading')
                }),
                exhaustMap(({location, onSuccess}) => {
                    const clinicId = Number(
                        location?.clinic_id ??
                        store.activeClinicId() ??
                        store.userFacade.clinic_id() ??
                        0
                    )
                    const locationId = Number(location?.id ?? 0)

                    return store.clinicService.setPrimaryLocation(clinicId, locationId).pipe(
                        tapResponse({
                            next: (response: any) => {
                                const updatedLocation = response?.location ?? response?.data ?? response
                                const primaryLocationId = Number(
                                    updatedLocation?.id ??
                                    updatedLocation?.clinic_location_id ??
                                    locationId
                                )

                                const updatedLocations = Object.values(store.entities()).map((existing: any) => {
                                    const isPrimary = Number(existing.id) === primaryLocationId

                                    return {
                                        ...existing,
                                        ...(isPrimary ? updatedLocation : {}),
                                        id: Number(existing.id),
                                        clinic_id: Number(existing.clinic_id ?? clinicId),
                                        primary_location: isPrimary,
                                        is_primary: isPrimary,
                                        primary: isPrimary
                                    }
                                })

                                patchState(store, setAllEntities(updatedLocations))
                                store.clinicStore.setAllLocations(updatedLocations)
                                store.setLocationListStatus('success')
                                onSuccess?.()
                            },
                            error: (err: unknown) => {
                                store.setError(true, extractErrorMessage(err))
                                store.setLocationListStatus('error')
                            }
                        })
                    )
                })
            )
        ),
        deleteClinicLocation: rxMethod<{location: ClinicLocation, hard_delete?: boolean}>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setLocationListStatus('loading')
                }),
                exhaustMap(({location, hard_delete = false}) => 
                    store.clinicService.deleteLocation(location, hard_delete).pipe(
                        tapResponse({
                            next: (response: any) => {
                                const locations = store.locations().filter((a) => a.id !== location.id)
                                patchState(store, setAllEntities(locations))
                                store.clinicStore.setAllLocations(locations)
                                store.setLocationListStatus('success')
                            },
                            error: (err: unknown) => {
                                store.setError(true, extractErrorMessage(err))
                                store.setLocationListStatus('error')
                            }
                        })
                    )
                )
            )
        )
    }))
        
)